import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { InfoDialogComponent, InfoDialogData, LoadingDialogComponent, LoadingDialogData } from '@shared/ui';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { RoleSwitchersComponent } from '@telespot/authentication/ui';
import { WorkspaceItem } from '@telespot/domain';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { WorkspaceSelectorComponent } from '../../workspace/widgets/workspace-selector/workspace-selector.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserEditorFacade } from './user-editor.facade';
import { userEditorFacadeProviders } from './user-editor.facade.providers';
import { AuthService } from '@telespot/web-core';
import { RoleData } from '@telespot/authentication/ui';

@Component({
    selector: 'ts-user-editor',
    templateUrl: './user-editor.component.html',
    styleUrls: ['./user-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [...userEditorFacadeProviders()],
    imports: [
        CommonModule,
        MatDialogModule,
        TranslateModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        RoleSwitchersComponent,
        WorkspaceSelectorComponent,
    ]
})
export class UserEditorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public initialWorkspaceSearchTerm: string | undefined;

  constructor(
    public readonly facade: UserEditorFacade,
    private readonly authService: AuthService,
    private readonly _dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  public ngOnInit() {
    const org = this.authService.currentOrganizationValue;

    // Capture the initial search term value
    this.facade.workspaces$.pipe(take(1)).subscribe(() => {
      this.initialWorkspaceSearchTerm = '';
    });

    // Initialize facade with organization context
    this.facade.initialize({ organizationId: org.id, maxUsers: org.license.maxUsers });

    // Select the user for editing
    this.facade.selectUser({
      userId: this.route.snapshot.params.id,
      organizationId: org.id,
      maxUsers: org.license.maxUsers,
    });
  }

  public get roles$(): Observable<RoleData[]> {
    return this.facade.roles$;
  }

  public get loadingRoles$(): Observable<boolean> {
    return this.facade.rolesLoading$;
  }

  public ngOnDestroy() {
    this.facade.resetSelectedUserState();
    this.destroy$.next();
  }

  public loadMoreWorkspaces(hasMoreItems: boolean) {
    this.facade.loadMoreWorkspaces(hasMoreItems);
  }

  public loadAllWorkspaces() {
    this.facade.loadAllWorkspaces();
  }

  public searchWorkspace(term: string) {
    this.facade.searchWorkspace(term);
  }

  public addToWorkspaces(workspaces: WorkspaceItem[]) {
    this.facade.addToWorkspaces(workspaces);
  }

  public removeFromWorkspaces(workspaces: WorkspaceItem[]) {
    this.facade.removeFromWorkspaces(workspaces);
  }

  public update() {
    const data: LoadingDialogData = {
      message: 'dialog.saving_user',
      icon: 'dialog.saving_user',
    };

    const dialog = this._dialog.open(LoadingDialogComponent, { data, disableClose: true });

    dialog
      .afterOpened()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.facade.syncSelectedUser());

    dialog
      .afterClosed()
      .pipe(withLatestFrom(this.facade.error$), takeUntil(this.destroy$))
      .subscribe(([, error]) => (error ? this.openErrorInfoDialog(error) : this.openSuccessInfoDialog()));

    this.facade.isSynced$
      .pipe(
        debounceTime(500),
        filter((isSynced) => isSynced),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => dialog.close());
  }

  public navigateToUsers() {
    this.router.navigateByUrl('users');
  }

  private openSuccessInfoDialog() {
    this.openInfoDialog({
      title: 'dialog.user_update_success_title',
      text: 'dialog.user_update_success_text',
      buttonText: 'button.back_to_user',
    });
  }

  private openErrorInfoDialog(error?: Error | string) {
    this.openInfoDialog({
      title: 'dialog.user_update_error_title',
      text: error instanceof Error ? error.message : error,
      buttonText: 'button.back_to_user',
    });
  }

  private openInfoDialog(data: InfoDialogData) {
    const dialogRef = this._dialog.open(InfoDialogComponent, { data, disableClose: true });

    dialogRef
      .afterClosed()
      .pipe(
        filter(({ dismissed }) => !dismissed),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.navigateToUsers());
  }
}
